import { useState, useEffect, useRef, useCallback } from "react";
import dayjs from "dayjs";
import axios from "axios";
import { ClientResponseInteraction, InteractionOrder, PastInteraction } from "../types/interactions";
import { getThreeBusinessDaysAgo } from "../constants";

const DEBOUNCE_MS = 1000;

interface UseClientResponseInteractionsReturn {
  interactions: ClientResponseInteraction[];
  isLoading: boolean;
  error: string | null;
  fromDate: dayjs.Dayjs;
  toDate: dayjs.Dayjs;
  setFromDate: (date: dayjs.Dayjs) => void;
  setToDate: (date: dayjs.Dayjs) => void;
  updateResponse: (id: number, responseStatus: string | null, responseNote: string | null) => void;
}

export const useClientResponseInteractions = (enabled: boolean): UseClientResponseInteractionsReturn => {
  const [fromDate, setFromDate] = useState<dayjs.Dayjs>(getThreeBusinessDaysAgo());
  const [toDate, setToDate] = useState<dayjs.Dayjs>(dayjs());
  const [interactions, setInteractions] = useState<ClientResponseInteraction[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const debounceTimers = useRef<Record<number, ReturnType<typeof setTimeout>>>({});

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }

    const abortController = new AbortController();

    const fetch = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const from = parseInt(fromDate.format("YYYYMMDD"));
        const to = parseInt(toDate.format("YYYYMMDD"));

        const { data } = await axios.get<ClientResponseInteraction[]>(
          `/api/clientphonebook/contactInteractions/getByInteractionReasons?fromDate=${from}&toDate=${to}`,
          { signal: abortController.signal }
        );

        const uniqueOrders = [
          ...new Map(
            data
              .filter((i) => i.orderNumber)
              .map((i) => [`${i.orderNumber}:${i.interactionCategory}`, i])
          ).values()
        ];

        const orderMap = new Map<string, InteractionOrder>();
        const contactCountsMap = new Map<number, number>();

        await Promise.all([
          uniqueOrders.length > 0
            ? Promise.all(
                uniqueOrders.map(async (interaction) => {
                  try {
                    const primaryStatus = interaction.interactionCategory ?? "Received";
                    const fallbackStatus = primaryStatus === "Closed" ? "Received" : "Closed";

                    const { data: orders } = await axios.get<InteractionOrder[]>(
                      "/api/interactions/GetByOrderNumber",
                      {
                        params: { orderNumber: interaction.orderNumber, status: primaryStatus },
                        signal: abortController.signal
                      }
                    );

                    if (orders.length > 0) {
                      orderMap.set(interaction.orderNumber, orders[0]);
                    } else {
                      const { data: fallbackOrders } = await axios.get<InteractionOrder[]>(
                        "/api/interactions/GetByOrderNumber",
                        {
                          params: { orderNumber: interaction.orderNumber, status: fallbackStatus },
                          signal: abortController.signal
                        }
                      );
                      if (fallbackOrders.length > 0) {
                        orderMap.set(interaction.orderNumber, fallbackOrders[0]);
                      }
                    }
                  } catch {}
                })
              )
            : Promise.resolve(),
          data.length > 0
            ? Promise.all(
                [...new Set(data.map((interaction) => interaction.contactId).filter((contactId) => contactId > 0))]
                  .map(async (contactId) => {
                    try {
                      const { data: pastInteractions } = await axios.get<PastInteraction[]>(
                        "/api/clientphonebook/contactInteractions/getPastInteractions",
                        {
                          params: { contactId },
                          signal: abortController.signal
                        }
                      );
                      contactCountsMap.set(contactId, pastInteractions.length);
                    } catch {}
                  })
              )
            : Promise.resolve()
        ]);

        const merged = data.map((i) => {
          const order = orderMap.get(i.orderNumber);
          return {
            ...i,
            companyName: order?.companyName ?? null,
            isClient: order?.isClient ?? null,
            isDealOwner: order?.isDealOwner ?? null,
            pastInteractionsCount: contactCountsMap.get(i.contactId) ?? 0
          };
        });

        setInteractions(merged);
      } catch (err) {
        if (axios.isCancel(err) || (err instanceof DOMException && err.name === "AbortError")) return;
        setError("Failed to load interactions");
      } finally {
        if (!abortController.signal.aborted) setIsLoading(false);
      }
    };

    fetch();
    return () => abortController.abort();
  }, [enabled, fromDate, toDate]);

  const updateResponse = useCallback((id: number, responseStatus: string | null, responseNote: string | null) => {
    setInteractions((prev) =>
      prev.map((i) => (i.id === id ? { ...i, responseStatus, responseNote } : i))
    );

    if (debounceTimers.current[id]) clearTimeout(debounceTimers.current[id]);
    debounceTimers.current[id] = setTimeout(async () => {
      try {
        await axios.patch(`/api/clientphonebook/contactInteractions/${id}/response`, {
          responseStatus,
          responseNote
        });
      } catch {}
    }, DEBOUNCE_MS);
  }, []);

  return { interactions, isLoading, error, fromDate, toDate, setFromDate, setToDate, updateResponse };
};
